(() => {
  const STORAGE_KEY = "wc:browsing-history:v1";
  const MAX_PRODUCTS = 6;
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/i;

  class WcBrowsingHistory extends HTMLElement {
    connectedCallback() {
      if (this.abortController && !this.abortController.signal.aborted) return;

      this.abortController = new AbortController();
      this.renderHistory();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    readHistory() {
      try {
        const history = JSON.parse(window.localStorage.getItem(STORAGE_KEY));

        if (!Array.isArray(history)) return [];

        return history.filter(
          (handle) => typeof handle === "string" && HANDLE_PATTERN.test(handle),
        );
      } catch (error) {
        return [];
      }
    }

    writeHistory(history) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
      } catch (error) {
        // Browsing history is an enhancement; the carousel can still render
        // the current product when storage is unavailable.
      }
    }

    getDisplayHistory() {
      const currentHandle = this.dataset.productHandle;
      const previousHistory = this.readHistory();
      const uniqueHistory = [...new Set(previousHistory)].slice(
        0,
        MAX_PRODUCTS,
      );

      if (!currentHandle || !HANDLE_PATTERN.test(currentHandle)) {
        return uniqueHistory;
      }

      const displayHistory = [
        ...uniqueHistory.filter((handle) => handle !== currentHandle),
      ].slice(0, MAX_PRODUCTS);
      const updatedHistory = [currentHandle, ...displayHistory].slice(
        0,
        MAX_PRODUCTS,
      );

      this.writeHistory(updatedHistory);
      return displayHistory;
    }

    async fetchProductSlide(handle) {
      const root = window.Shopify?.routes?.root || "/";
      const productUrl = new URL(
        `${root}products/${encodeURIComponent(handle)}`,
        window.location.origin,
      );

      productUrl.searchParams.set("view", "browsing-history-card");

      const response = await fetch(productUrl, {
        credentials: "same-origin",
        headers: { Accept: "text/html" },
        signal: this.abortController.signal,
      });

      if (!response.ok) return null;

      const documentFragment = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );

      return (
        documentFragment.querySelector("[data-wc-browsing-history-card]") ||
        null
      );
    }

    async getProductSlide(handle) {
      try {
        return await this.fetchProductSlide(handle);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn(`Unable to load recently viewed product: ${handle}`);
        }

        return null;
      }
    }

    async renderHistory() {
      const slider = this.querySelector("[data-wc-browsing-history-slides]");
      const abortController = this.abortController;

      if (!slider) return;

      const history = this.getDisplayHistory();
      const slides = await Promise.all(
        history.map((handle) => this.getProductSlide(handle)),
      );

      if (!this.isConnected || abortController.signal.aborted) return;

      slider.replaceChildren(...slides.filter(Boolean));
      this.setAttribute("aria-busy", "false");

      if (!slider.children.length) return;

      this.hidden = false;

      window.yotpoWidgetsContainer?.initWidgets?.();
    }
  }

  if (!customElements.get("wc-browsing-history")) {
    customElements.define("wc-browsing-history", WcBrowsingHistory);
  }
})();
